import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from 'shared/services/api/axiosInstance';
import { useTeam } from 'shared/context/teamContext';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { usePermissions } from 'shared/hooks/usePermissions';
import { PERMISSIONS } from 'shared/constants/permissions';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { Section, KV, btnPrimary, btnNeutral } from 'features/settings/components/panels/askCreditsShared';

const publicAsset = (p) => `${(process.env.PUBLIC_URL || '').replace(/\/?$/, '')}${p}`;

/**
 * Settings → Organization → Slack.
 * One Slack workspace per Trodo organization, shared by every team in it.
 */
export default function OrgSlackPanel() {
  const { selectedTeam } = useTeam();
  const location = useLocation();
  const dialog = useAppDialog();
  const { can } = usePermissions();
  const teamId = selectedTeam?.id || selectedTeam?._id || localStorage.getItem('selectedTeam');
  const canManage = can(PERMISSIONS.INTEGRATIONS_DEVELOPER_MANAGE);

  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/integrations/slack/org', {
        params: { team_id: teamId },
      });
      setConn(data);
      setError('');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load Slack status');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('org_slack') || params.get('slack');
    if (status === 'connected') {
      setBanner({ kind: 'ok', text: 'Slack connected for this organization.' });
      load();
    } else if (status === 'error') {
      const reason = params.get('reason') || 'unknown';
      if (reason === 'workspace_linked_elsewhere') {
        const holder = params.get('linked_org') || params.get('linked_team');
        const workspace = params.get('linked_workspace');
        const where = holder ? ` to ${holder}` : ' elsewhere';
        const which = workspace ? ` ${workspace}` : '';
        setBanner({
          kind: 'err',
          text: `The Slack workspace${which} is already connected${where}. Disconnect it there first, or pick a different workspace on the Slack screen.`,
        });
      } else {
        setBanner({ kind: 'err', text: `Slack connection failed: ${reason}` });
      }
    }
  }, [location.search, load]);

  const connect = async () => {
    setBusy(true);
    setError('');
    try {
      const return_to = `${window.location.pathname}${window.location.search || ''}`;
      const { data } = await axiosInstance.post(
        '/integrations/slack/org/start',
        { return_to },
        { params: { team_id: teamId } },
      );
      if (data?.url) window.location.href = data.url;
      else setError('Slack connect URL missing');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to start Slack connect');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    const ok = await dialog.confirm(
      'Every team in this organization will lose Slack until you reconnect.',
      { title: 'Disconnect Slack?', confirmLabel: 'Disconnect', cancelLabel: 'Cancel', danger: true },
    );
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await axiosInstance.delete('/integrations/slack/org', { params: { team_id: teamId } });
      await load();
      setBanner({ kind: 'ok', text: 'Slack disconnected.' });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const connected = !!conn?.connected;
  const expired = connected && (conn.needs_reconnect || conn.needs_reauthorize);

  return (
    <div className="w-full">
      {banner ? (
        <p
          role="status"
          className="mb-4 text-[12px]"
          style={{ color: banner.kind === 'ok' ? 'var(--feedback-success)' : 'var(--feedback-error)' }}
        >
          {banner.text}
        </p>
      ) : null}

      <Section title="Workspace">
        {loading && !conn ? (
          <BrandLoader size="md" label="Loading Slack" />
        ) : (
          <>
            <KV label="Status">
              {expired ? (
                <span style={{ color: 'var(--feedback-warning)' }}>Reconnect needed</span>
              ) : connected ? (
                <span style={{ color: 'var(--feedback-success)' }}>Connected</span>
              ) : (
                'Not connected'
              )}
            </KV>
            {connected ? (
              <KV label="Workspace">
                <span className="inline-flex items-center gap-2">
                  <img
                    src={publicAsset('/icons/slack.png')}
                    alt=""
                    aria-hidden
                    className="h-4 w-4 shrink-0 object-contain"
                  />
                  {conn.slack_workspace_name || conn.slack_workspace_id}
                </span>
              </KV>
            ) : null}
          </>
        )}

        {!loading || conn ? (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!connected || expired ? (
              <button
                type="button"
                onClick={connect}
                disabled={busy || !canManage}
                title={canManage ? undefined : 'Owners, admins and developers only'}
                className={btnPrimary}
              >
                {busy ? 'Opening…' : expired ? 'Reconnect' : 'Connect Slack'}
              </button>
            ) : null}
            {connected ? (
              <button
                type="button"
                onClick={disconnect}
                disabled={busy || !canManage}
                title={canManage ? undefined : 'Owners, admins and developers only'}
                className={btnNeutral}
              >
                Disconnect
              </button>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p className="mt-3 text-[12px]" style={{ color: 'var(--feedback-error)' }}>{error}</p>
        ) : null}
      </Section>
    </div>
  );
}
